import { PropsWithChildren, ComponentType } from 'react';
import styled, { css } from 'styled-components';
import { BouSpacingLevel, fluidSpacing } from '../../common/generic/layout/bou-spacing';
import { BouPageWithFrame } from '../../common/generic/layout/bou-tool-page-with-frame';
import { desktop993pxOrWider } from '../../common/generic/layout/breakpoint-utils';
import { appHeaderHeight, INTERNAL_BREADCRUMBS_HEIGHT_PX } from '../../common/generic/layout/layout-constants';

/**
 * Most /internal pages render the full-width `InternalBreadcrumbs` bar as their
 * title, so the frame reserves its height (`hasTitleBar`). Pages that don't show
 * the breadcrumb bar (and provide their own in-content title instead) pass
 * `hasTitleBar: false` so no empty band is reserved below the global header.
 */
export function withEmptyFrame<T extends object>(
  Component: ComponentType<PropsWithChildren<T>>,
  options?: { hasTitleBar?: boolean },
) {
  const hasTitleBar = options?.hasTitleBar ?? true;
  const ComponentWithEmptyFrame = (props: T) => {
    return (
      <BouPageWithFrame
        sideBarContent={<></>}
        hideSideBar={true}
        hasTitleBar={hasTitleBar}
        titleBarHeight={INTERNAL_BREADCRUMBS_HEIGHT_PX}
      >
        <ContentWrap>
          <Component {...props} />
        </ContentWrap>
      </BouPageWithFrame>
    );
  };
  return ComponentWithEmptyFrame;
}

const ContentWrap = styled.div`
  ${desktop993pxOrWider(css`
    ${fluidSpacing('padding-bottom', BouSpacingLevel.LARGE)}
  `)}
`;

export function withEmptyFrameNoPadding<T extends object>(Component: ComponentType<PropsWithChildren<T>>) {
  const ComponentWithEmptyFrame = (props: T) => {
    return (
      <BouPageWithFrame
        sideBarContent={<></>}
        hideSideBar={true}
        hasTitleBar={true}
        titleBarHeight={INTERNAL_BREADCRUMBS_HEIGHT_PX}
      >
        <Component {...props} />
      </BouPageWithFrame>
    );
  };
  return ComponentWithEmptyFrame;
}

/** Full-screen frame for the chat view — no sidebar, no status bar. */
export function withChatFrame<T extends object>(Component: ComponentType<PropsWithChildren<T>>) {
  const ComponentWithChatFrame = (props: T) => {
    return (
      <ChatFrameRoot>
        <Component {...props} />
      </ChatFrameRoot>
    );
  };
  return ComponentWithChatFrame;
}

const ChatFrameRoot = styled.div`
  position: fixed;
  top: ${appHeaderHeight};
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/**
 * Full-viewport frame for the `/internal/proto/` prototypes — no sidebar, no status
 * bar, and no header band reserved, because `header-maybe.tsx` renders no header on
 * those routes at all.
 *
 * That is the difference from `withChatFrame`, and it is the whole point of the
 * frame: a prototype of a launcher or a dashboard proposes its own top navigation,
 * which is usually the part under review, so the harness cannot spend the top of the
 * window on chrome of its own. The switcher docks at the bottom instead
 * (`proto-harness.tsx`).
 */
export function withProtoFrame<T extends object>(Component: ComponentType<PropsWithChildren<T>>) {
  const ComponentWithProtoFrame = (props: T) => {
    return (
      <ProtoFrameRoot>
        <Component {...props} />
      </ProtoFrameRoot>
    );
  };
  return ComponentWithProtoFrame;
}

const ProtoFrameRoot = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
